import { useState } from 'react';
import { useUser } from '../../auth/AuthProvider';
import { isManager } from '../../data/domain';
import { useJobRecords, useJobs } from '../../data/hooks';
import type { JobRecord } from '../../data/types';
import { dateTime, plural, shortDate } from '../../lib/format';
import { deleteEntity } from '../../sync/outbox';
import { useUI } from '../../ui/UIProvider';
import { Icon } from '../../ui/Icon';
import { EmptyState, PageHeading, View } from '../../ui/View';
import { downloadRecord } from './recordDocument';

/** Compliance archive: every saved job record, grouped by service date and technician. */
export function RecordsView() {
  const user = useUser();
  const ui = useUI();
  const records = useJobRecords();
  const jobs = useJobs();
  const [query, setQuery] = useState('');
  const canDelete = isManager(user);

  const dateFor = (record: JobRecord) => jobs.find((job) => job.id === record.jobId)?.date || record.jobDate || '';
  const term = query.trim().toLowerCase();
  const groups = new Map<string, { date: string; technician: string; records: JobRecord[] }>();
  for (const record of records) {
    const date = dateFor(record);
    if (term && !`${record.clientName} ${record.technician} ${record.jobService} ${record.type} ${dateTime(record.createdAt)} ${date}`.toLowerCase().includes(term)) continue;
    const technician = record.technician || 'Technician not recorded';
    const key = `${date}|${technician}`;
    if (!groups.has(key)) groups.set(key, { date, technician, records: [] });
    groups.get(key)!.records.push(record);
  }
  const sorted = [...groups.values()].sort((a, b) => `${b.date}|${b.technician}`.localeCompare(`${a.date}|${a.technician}`));

  async function remove(record: JobRecord) {
    if (!window.confirm(`Delete the ${record.type} for ${record.clientName}? This removes the saved record from the archive.`)) return;
    await deleteEntity('job-records', record.id, `Deleted ${record.type} for ${record.clientName}`);
  }

  return (
    <View id="documents">
      <PageHeading eyebrow="Compliance records" title="Job records" subtitle="Keep ARMPs, pre-treatment advice, post-treatment advice and completed service records together for compliance.">
      </PageHeading>
      <div className="toolbar">
        <label className="search-field toolbar-grow">
          <Icon name="search" />
          <input id="recordsSearch" type="search" placeholder="Search client, technician, service or record" aria-label="Search job records" value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <p className="hint toolbar-hint">{plural(sorted.reduce((sum, group) => sum + group.records.length, 0), 'record')}</p>
      </div>
      <div className="panel" id="activityRecords">
        {sorted.length ? sorted.map((group) => (
          <section className="record-group" key={`${group.date}|${group.technician}`}>
            <header className="record-group-header"><b>{shortDate(group.date) || 'Date not recorded'}</b><small>{group.technician} · {plural(group.records.length, 'record')}</small></header>
            <div className="row-list">
              {group.records.map((record) => (
                <article className="row record-row" key={record.id}>
                  <span className="row-text"><b>{record.clientName}</b><small>{record.type} · {record.jobService || 'Service record'} · {dateTime(record.createdAt)}</small></span>
                  <span className="button-group">
                    <button className="btn btn-secondary btn-sm" onClick={() => ui.open('recordPreview', { recordId: record.id })}>View</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => downloadRecord(record)}>Download</button>
                    {canDelete && <button className="btn btn-danger btn-sm" onClick={() => remove(record)}>Delete</button>}
                  </span>
                </article>
              ))}
            </div>
          </section>
        )) : <EmptyState>{records.length ? 'No records match this search.' : 'No job compliance records have been created yet. Records are added when advice forms, ARMPs and completed services are saved.'}</EmptyState>}
      </div>
    </View>
  );
}
